import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import { apiService } from "../../../services/api";
import { Produto } from "../../../types/produtos";
import { EmpenhoFormatado, Empenho, SituacaoEmpenho, SaldoItemContrato } from "../../../types/empenhos";
import { CreateContaReceberData } from "../../../types/financeiro";
import { Contrato, ContratoDetalhado } from "../../../types/contratos";
import { usePermissions } from "../../../hooks/usePermissions";
import { ProtectedValue } from "../../../components/ProtectedValue";

interface ItemEmpenhoForm {
    id_produto: number;
    id_produto_oportunidade: number;
    quantidade: number;
    valor_unitario: number;
    produto_nome?: string;
}

interface ProdutoOportunidade {
    id: number;
    id_produto: number;
    id_oportunidade: number;
    quantidade: number;
    valor_unitario: number;
    valor_proposta: number; // Adicionado o campo valor_proposta
    produto_nome: string;
    produto: Produto; // Informações completas do produto
}

interface ModalEmpenhoProps {
    show: boolean;
    onHide: () => void;
    contratoId: number;
    empenho: EmpenhoFormatado | null;
    onSave: () => void;
}

const ModalEmpenho: React.FC<ModalEmpenhoProps> = ({
    show,
    onHide,
    contratoId,
    empenho,
    onSave
}) => {
    const [formData, setFormData] = useState<{
        id_contrato: number;
        codigo_empenho: string;
        data_empenho: Date;
        data_previsao_pagamento: Date;
        valor_total: string;
        situacao: SituacaoEmpenho;
        descricao: string;
        itens: ItemEmpenhoForm[];
    }>({
        id_contrato: contratoId,
        codigo_empenho: "",
        data_empenho: new Date(),
        data_previsao_pagamento: new Date(new Date().setMonth(new Date().getMonth() + 1)),
        valor_total: "",
        situacao: "Pendente" as SituacaoEmpenho,
        descricao: "",
        itens: [] as ItemEmpenhoForm[]
    });

    const { hasPermission } = usePermissions();
    const podeVerValores = hasPermission('AUTORIZACOES', 'VER_VALORES');

    const [produtos, setProdutos] = useState<Produto[]>([]);
    const [produtosOportunidade, setProdutosOportunidade] = useState<ProdutoOportunidade[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [manualValorTotal, setManualValorTotal] = useState(false);
    const [initialLoad, setInitialLoad] = useState(true);
    const [idLicitanteDoContrato, setIdLicitanteDoContrato] = useState<number | null>(null);
    const [oportunidadeId, setOportunidadeId] = useState<number | null>(null);
    const [saldosContrato, setSaldosContrato] = useState<Record<number, SaldoItemContrato>>({});
    // Quantidade já salva no empenho atual (edit mode) para descontar do saldo
    const [qtdOriginaisEmpenho, setQtdOriginaisEmpenho] = useState<Record<number, number>>({});

    const isEditMode = !!empenho;

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        }).format(value);
    };

    // Função para carregar produtos da oportunidade
    const loadProdutosOportunidade = async (idOportunidade: number): Promise<ProdutoOportunidade[]> => {
        try {
            console.log("Carregando produtos da oportunidade:", idOportunidade);

            // Assuming you have an API endpoint to get products of an opportunity
            const produtosOppRes = await apiService.getProdutosOportunidade(idOportunidade);

            if (produtosOppRes.success && produtosOppRes.data) {
                console.log("Produtos da oportunidade carregados:", produtosOppRes.data);

                // Enriquecer com dados completos dos produtos em 1 chamada batch
                const ids = produtosOppRes.data.map((p: any) => p.id_produto);
                const produtosMap: Record<number, any> = {};
                try {
                    const batchRes = await apiService.getProdutosByIds(ids);
                    if (batchRes.success && Array.isArray(batchRes.data)) {
                        batchRes.data.forEach((p: any) => { produtosMap[p.id] = p; });
                    }
                } catch (error) {
                    console.error('Erro ao carregar produtos em lote:', error);
                }

                const produtosEnriquecidos = produtosOppRes.data.map((produtoOpp: any) => {
                    const produto = produtosMap[produtoOpp.id_produto];
                    return produto
                        ? { ...produtoOpp, produto_nome: produto.nome, produto }
                        : produtoOpp;
                });

                setProdutosOportunidade(produtosEnriquecidos);
                return produtosEnriquecidos;
            }
        } catch (error) {
            console.error("Erro ao carregar produtos da oportunidade:", error);
            toast.error("Erro ao carregar produtos da oportunidade.");
        }
        return [];
    };

    useEffect(() => {
        if (!show) {
            setInitialLoad(true);
            setIdLicitanteDoContrato(null);
            setOportunidadeId(null);
            setProdutosOportunidade([]);
            setSaldosContrato({});
            setQtdOriginaisEmpenho({});
            return;
        }

        if (initialLoad) {
            if (isEditMode && empenho) {
                // Logic for editing existing empenho
                setManualValorTotal(true);
                const loadEditData = async () => {
                    try {
                        const [contratoRes, saldosRes, empenhoDetalhes] = await Promise.all([
                            apiService.getContratoById(contratoId),
                            apiService.getSaldoMestreContrato(contratoId),
                            apiService.getEmpenhoById(empenho.id),
                        ]);

                        if (contratoRes.success && contratoRes.data) {
                            setIdLicitanteDoContrato(contratoRes.data.id_licitante);
                            if (contratoRes.data.id_oportunidade) {
                                setOportunidadeId(contratoRes.data.id_oportunidade);
                                await loadProdutosOportunidade(contratoRes.data.id_oportunidade);
                            }
                        }

                        if (saldosRes.success && saldosRes.data) {
                            const map: Record<number, SaldoItemContrato> = {};
                            saldosRes.data.forEach((s: SaldoItemContrato) => { map[s.id_produto_oportunidade] = s; });
                            setSaldosContrato(map);
                        }

                        if (empenhoDetalhes.success && empenhoDetalhes.data) {
                            const { itens, ...empenhoData } = empenhoDetalhes.data;
                            const situacaoAtual = empenho.situacao || empenhoData.situacao || "Pendente";

                            if (itens) {
                                const qtdOriginais: Record<number, number> = {};
                                itens.forEach((item: any) => {
                                    const idPO = item.id_produto_oportunidade || 0;
                                    qtdOriginais[idPO] = (qtdOriginais[idPO] || 0) + Number(item.quantidade || 0);
                                });
                                setQtdOriginaisEmpenho(qtdOriginais);
                            }

                            setFormData({
                                id_contrato: contratoId,
                                codigo_empenho: empenho.codigo_empenho || "",
                                data_empenho: new Date(empenho.data_empenho),
                                data_previsao_pagamento: empenho.data_previsao_pagamento ? new Date(empenho.data_previsao_pagamento) : new Date(new Date().setMonth(new Date().getMonth() + 1)),
                                valor_total: empenho.valor_total.toString(),
                                situacao: situacaoAtual,
                                descricao: empenho.descricao || "",
                                itens: itens ? itens.map((item: any) => ({
                                    id_produto: item.id_produto,
                                    id_produto_oportunidade: item.id_produto_oportunidade || 0,
                                    quantidade: item.quantidade,
                                    valor_unitario: item.valor_unitario,
                                    produto_nome: item.produto_nome
                                })) : []
                            });
                        }
                    } catch (error) {
                        console.error("Erro ao carregar dados para edição:", error);
                        toast.error("Erro ao carregar dados do empenho.");
                    }
                    setInitialLoad(false);
                };
                loadEditData();
            } else if (!isEditMode) {
                // Logic for creating new empenho
                const initializeNewData = async () => {
                    try {
                        const [contratoRes, saldosRes] = await Promise.all([
                            apiService.getContratoById(contratoId),
                            apiService.getSaldoMestreContrato(contratoId),
                        ]);

                        if (saldosRes.success && saldosRes.data) {
                            const map: Record<number, SaldoItemContrato> = {};
                            saldosRes.data.forEach((s: SaldoItemContrato) => { map[s.id_produto_oportunidade] = s; });
                            setSaldosContrato(map);
                        }

                        if (contratoRes.success && contratoRes.data) {
                            setIdLicitanteDoContrato(contratoRes.data.id_licitante);

                            if (contratoRes.data.id_oportunidade) {
                                setOportunidadeId(contratoRes.data.id_oportunidade);

                                const produtosOpp = await loadProdutosOportunidade(contratoRes.data.id_oportunidade);

                                // Initialize form with first product from opportunity if available
                                if (produtosOpp.length > 0) {
                                    const primeiroProduto = produtosOpp[0];
                                    setFormData({
                                        id_contrato: contratoId,
                                        codigo_empenho: "",
                                        data_empenho: new Date(),
                                        data_previsao_pagamento: new Date(new Date().setMonth(new Date().getMonth() + 1)),
                                        valor_total: "",
                                        situacao: "Pendente",
                                        descricao: "",
                                        itens: [{
                                            id_produto: primeiroProduto.id_produto,
                                            id_produto_oportunidade: primeiroProduto.id,
                                            quantidade: 1,
                                            valor_unitario: primeiroProduto.valor_proposta || primeiroProduto.valor_unitario,
                                            produto_nome: primeiroProduto.produto_nome
                                        }]
                                    });
                                } else {
                                    // Se não há produtos na oportunidade, initialize empty
                                    setFormData({
                                        id_contrato: contratoId,
                                        codigo_empenho: "",
                                        data_empenho: new Date(),
                                        data_previsao_pagamento: new Date(new Date().setMonth(new Date().getMonth() + 1)),
                                        valor_total: "",
                                        situacao: "Pendente",
                                        descricao: "",
                                        itens: []
                                    });
                                    toast.warn("Esta oportunidade não possui produtos cadastrados.");
                                }
                            } else {
                                toast.error("Contrato não está vinculado a uma oportunidade.");
                            }
                        } else {
                            toast.error("Falha ao carregar detalhes do contrato.");
                        }
                    } catch (error: any) {
                        console.error("Erro ao carregar contrato:", error);
                        toast.error(`Erro ao carregar detalhes do contrato: ${error.message || 'Erro desconhecido'}`);
                    }

                    setManualValorTotal(false);
                    setInitialLoad(false);
                };
                initializeNewData();
            }
        }
    }, [show, contratoId, empenho, isEditMode, initialLoad]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDateChange = (date: Date | null, fieldName: string) => {
        if (date) {
            setFormData(prev => ({ ...prev, [fieldName]: date }));
        }
    };

    const adicionarItemEmpenho = () => {
        if (produtosOportunidade.length === 0) {
            toast.error("Não há produtos disponíveis nesta oportunidade para adicionar.");
            return;
        }
        const produtoPadrao = produtosOportunidade[0];
        setFormData(prev => ({
            ...prev,
            itens: [
                ...prev.itens,
                {
                    id_produto: produtoPadrao.id_produto,
                    id_produto_oportunidade: produtoPadrao.id,
                    quantidade: 1,
                    valor_unitario: produtoPadrao.valor_proposta || produtoPadrao.valor_unitario,
                    produto_nome: produtoPadrao.produto_nome
                }
            ]
        }));
    };

    const removerItemEmpenho = (index: number) => {
        setFormData(prev => ({
            ...prev,
            itens: prev.itens.filter((_, i) => i !== index)
        }));
    };

    const alterarItemEmpenho = (index: number, field: keyof ItemEmpenhoForm, value: any) => {
        setFormData(prev => {
            const novosItens = [...prev.itens];
            const currentItem = { ...novosItens[index] };

            if (field === 'id_produto') {
                const produtoSelecionado = produtosOportunidade.find(p => p.id_produto === parseInt(value));
                if (produtoSelecionado) {
                    currentItem.id_produto = produtoSelecionado.id_produto;
                    currentItem.id_produto_oportunidade = produtoSelecionado.id;
                    currentItem.valor_unitario = produtoSelecionado.valor_proposta || produtoSelecionado.valor_unitario;
                    currentItem.produto_nome = produtoSelecionado.produto_nome;
                    currentItem.quantidade = currentItem.quantidade || 1;
                }
            } else if (field === 'quantidade' || field === 'valor_unitario') {
                currentItem[field] = parseFloat(value) || 0;
            } else {
                (currentItem as any)[field] = value;
            }
            novosItens[index] = currentItem;
            return { ...prev, itens: novosItens };
        });
    };

    const calcularValorTotalEmpenho = () => {
        return formData.itens.reduce((total, item) => {
            return total + ((item.quantidade || 0) * (item.valor_unitario || 0));
        }, 0);
    };

    const toggleModoValorTotal = () => {
        if (manualValorTotal) {
            const valorTotalCalculado = calcularValorTotalEmpenho();
            setFormData(prev => ({
                ...prev,
                valor_total: valorTotalCalculado.toString()
            }));
        }
        setManualValorTotal(!manualValorTotal);
    };

    const handleValorTotalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
        setFormData(prev => ({ ...prev, valor_total: value }));
    };

    useEffect(() => {
        if (!manualValorTotal) {
            const valorTotal = calcularValorTotalEmpenho();
            setFormData(prev => ({
                ...prev,
                valor_total: valorTotal.toString()
            }));
        }
    }, [formData.itens, manualValorTotal]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            if (!formData.codigo_empenho.trim()) {
                toast.error("Informe o código do empenho.");
                setIsSubmitting(false);
                return;
            }
            if (formData.itens.length === 0) {
                toast.error("Adicione pelo menos um item ao empenho.");
                setIsSubmitting(false);
                return;
            }
            const valorTotalParsed = parseFloat(formData.valor_total);
            if (isNaN(valorTotalParsed) || valorTotalParsed <= 0) {
                toast.error("O valor total do empenho deve ser maior que zero.");
                setIsSubmitting(false);
                return;
            }

            // Validação de quantidade: não pode ultrapassar o disponível por produto
            for (const item of formData.itens) {
                const saldo = saldosContrato[item.id_produto_oportunidade];
                if (!saldo) continue;
                const qtdOriginal = isEditMode ? (qtdOriginaisEmpenho[item.id_produto_oportunidade] || 0) : 0;
                const disponivelParaEsteEmpenho = Number(saldo.quantidade_disponivel) + qtdOriginal;
                if (item.quantidade > disponivelParaEsteEmpenho) {
                    toast.error(
                        `"${item.produto_nome || saldo.produto_nome}": quantidade solicitada (${item.quantidade}) excede o disponível (${disponivelParaEsteEmpenho} un).`
                    );
                    setIsSubmitting(false);
                    return;
                }
            }

            const dadosEmpenho = {
                id_contrato: formData.id_contrato,
                is_master: true,
                codigo_empenho: formData.codigo_empenho,
                data_empenho: formData.data_empenho.toISOString().split('T')[0],
                data_previsao_pagamento: formData.data_previsao_pagamento.toISOString().split('T')[0],
                valor_total: valorTotalParsed,
                situacao: formData.situacao,
                descricao: formData.descricao,
                itens: formData.itens.map(item => ({
                    id_produto: item.id_produto,
                    id_produto_oportunidade: item.id_produto_oportunidade,
                    quantidade: item.quantidade,
                    valor_unitario: item.valor_unitario
                }))
            };

            if (isEditMode && empenho) {
                const resultadoUpdate = await apiService.updateEmpenho(empenho.id, dadosEmpenho);
                if (resultadoUpdate.success) {
                    toast.success("Empenho atualizado com sucesso!");
                    onSave();
                } else {
                    toast.error(resultadoUpdate.message || "Erro ao atualizar empenho.");
                }
            } else {
                const resultadoEmpenho = await apiService.createEmpenho(dadosEmpenho);
                if (resultadoEmpenho.success && resultadoEmpenho.data) {
                    toast.success("Empenho criado com sucesso!");
                    onSave();
                } else {
                    toast.error(resultadoEmpenho.message || "Erro ao criar empenho.");
                }
            }
        } catch (error: any) {
            toast.error(`Erro ao salvar empenho: ${error.message || 'Ocorreu um erro inesperado.'}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal
            show={show}
            onHide={onHide}
            size="lg"
            centered
            className="custom-modal"
            backdrop="static"
            keyboard={!isSubmitting}
        >
            <Modal.Header closeButton={!isSubmitting}>
                <Modal.Title>{isEditMode ? "Editar Empenho Mestre" : "Adicionar Empenho Mestre"}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <form onSubmit={handleSubmit}>
                    {/* Código do Empenho e Data do Empenho */}
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Código do Empenho <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className="form-control"
                                name="codigo_empenho"
                                value={formData.codigo_empenho}
                                onChange={handleChange}
                                placeholder="Ex: EMP-2025-001"
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Data do Empenho <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                                selected={formData.data_empenho}
                                onChange={(date) => handleDateChange(date, 'data_empenho')}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Data Prev. Pagamento */}
                    <div className="row">
                        <div className="col-md-6 mb-3">
                            <label className="col-form-label">
                                Data Prev. Pagamento <span className="text-danger">*</span>
                            </label>
                            <DatePicker
                                selected={formData.data_previsao_pagamento}
                                onChange={(date) => handleDateChange(date, 'data_previsao_pagamento')}
                                className="form-control"
                                dateFormat="dd/MM/yyyy"
                                required
                                disabled={isSubmitting}
                            />
                        </div>
                    </div>

                    {/* Descrição */}
                    <div className="mb-3">
                        <label className="col-form-label">Descrição</label>
                        <textarea
                            className="form-control"
                            rows={3}
                            name="descricao"
                            value={formData.descricao}
                            onChange={handleChange}
                            disabled={isSubmitting}
                        ></textarea>
                    </div>

                    {/* Itens do Empenho */}
                    <div className="mb-3">
                        <label className="col-form-label d-flex justify-content-between align-items-center">
                            <span>Itens do Empenho <span className="text-danger">*</span></span>
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={adicionarItemEmpenho}
                                disabled={produtosOportunidade.length === 0 || isSubmitting}
                            >
                                <i className="ti ti-plus me-1"></i> Adicionar Item
                            </button>
                        </label>

                        {produtosOportunidade.length === 0 && (
                            <div className="alert alert-warning text-center mt-2">
                                <i className="ti ti-alert-triangle me-2"></i>
                                Esta oportunidade não possui produtos cadastrados.
                            </div>
                        )}

                        {formData.itens.length === 0 && produtosOportunidade.length > 0 && (
                            <div className="alert alert-secondary text-center mt-2">Nenhum item adicionado.</div>
                        )}

                        {formData.itens.map((item, index) => {
                            const saldo = saldosContrato[item.id_produto_oportunidade];
                            const qtdOriginal = isEditMode ? (qtdOriginaisEmpenho[item.id_produto_oportunidade] || 0) : 0;
                            const disponivel = saldo
                                ? Number(saldo.quantidade_disponivel) + qtdOriginal
                                : null;

                            return (
                            <div key={index} className="card mb-2 border">
                                <div className="card-body p-2">
                                    <div className="row align-items-center">
                                        <div className="col-md-5 mb-2 mb-md-0">
                                            <label className="form-label visually-hidden">Produto</label>
                                            <select
                                                className="form-select form-select-sm"
                                                value={item.id_produto}
                                                onChange={(e) => alterarItemEmpenho(index, 'id_produto', e.target.value)}
                                                required
                                                disabled={isSubmitting}
                                            >
                                                <option value="" disabled>Selecione...</option>
                                                {produtosOportunidade.map(produtoOpp => (
                                                    <option key={produtoOpp.id} value={produtoOpp.id_produto}>
                                                        {produtoOpp.produto_nome}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="col-md-2 col-6 mb-2 mb-md-0">
                                            <label className="form-label visually-hidden">Quantidade</label>
                                            <input
                                                type="number"
                                                className="form-control form-control-sm"
                                                value={item.quantidade || ""}
                                                onChange={(e) => alterarItemEmpenho(index, 'quantidade', e.target.value)}
                                                step="0.01"
                                                min="0.01"
                                                placeholder="Qtde"
                                                required
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                        <div className="col-md-3 col-6 mb-2 mb-md-0">
                                            <label className="form-label visually-hidden">Valor Unitário</label>
                                            {podeVerValores ? (
                                                <input
                                                    type="number"
                                                    className="form-control form-control-sm"
                                                    value={item.valor_unitario || ""}
                                                    onChange={(e) => alterarItemEmpenho(index, 'valor_unitario', e.target.value)}
                                                    step="0.01"
                                                    min="0"
                                                    placeholder="Valor unit."
                                                    disabled={isSubmitting}
                                                />
                                            ) : (
                                                <input
                                                    className="form-control form-control-sm text-muted"
                                                    value="***"
                                                    readOnly
                                                    tabIndex={-1}
                                                />
                                            )}
                                        </div>
                                        <div className="col-md-2 d-flex align-items-center justify-content-end">
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-danger"
                                                onClick={() => removerItemEmpenho(index)}
                                                disabled={isSubmitting}
                                            >
                                                <i className="ti ti-trash"></i>
                                            </button>
                                        </div>
                                    </div>
                                    <div className="row mt-1 align-items-center">
                                        <div className="col">
                                            {disponivel !== null && (
                                                <small className={disponivel <= 0 ? 'text-danger' : 'text-success'}>
                                                    {disponivel <= 0 ? 'Totalmente empenhado' : `Disponível: ${disponivel} un`}
                                                </small>
                                            )}
                                        </div>
                                        <div className="col-auto text-end">
                                            <small>Subtotal: <ProtectedValue module="AUTORIZACOES" value={formatCurrency((item.quantidade || 0) * (item.valor_unitario || 0))} /></small>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>

                    {/* Valor Total e Edição Manual */}
                    <div className="row mb-3">
                        <div className="col-12">
                            <div className="d-flex justify-content-end align-items-center">
                                <div className="form-check me-3">
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        id="editarValorTotal"
                                        checked={manualValorTotal}
                                        onChange={toggleModoValorTotal}
                                        disabled={isSubmitting}
                                    />
                                    <label className="form-check-label" htmlFor="editarValorTotal">
                                        Editar valor total manualmente
                                    </label>
                                </div>

                                {manualValorTotal ? (
                                    <div className="input-group" style={{ maxWidth: '200px' }}>
                                        <span className="input-group-text">R$</span>
                                        <input
                                            type="text"
                                            className="form-control text-end"
                                            value={formData.valor_total}
                                            onChange={handleValorTotalChange}
                                            required
                                            disabled={isSubmitting}
                                            placeholder="0,00"
                                        />
                                    </div>
                                ) : (
                                    <h5>Valor Total: <ProtectedValue module="AUTORIZACOES" value={formatCurrency(parseFloat(formData.valor_total || "0"))} /></h5>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Botões de Ação */}
                    <div className="col-lg-12 text-end modal-btn mt-4 pt-3 border-top">
                        <button
                            type="button"
                            className="btn btn-light me-2"
                            onClick={onHide}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={isSubmitting || (produtosOportunidade.length === 0)}
                        >
                            {isSubmitting ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Salvando...
                                </>
                            ) : (
                                isEditMode ? 'Atualizar Empenho' : 'Salvar Empenho'
                            )}
                        </button>
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

export default ModalEmpenho;